/**
 * StatusBadge
 * Reusable badge component for displaying status with colors
 */

import { ReactNode } from 'react'
import type { AssignmentStatusV2, TgWorkStatus, TgInvoiceStatus, CustomerCommStatus, CustomerInvoiceStatus } from '../../types/statusTypes'
import {
  getAssignmentStatusBadgeClassesV2,
  getAssignmentStatusNameV2,
  getTgWorkStatusBadgeClasses,
  getTgWorkStatusName,
  getTgInvoiceStatusBadgeClasses,
  getTgInvoiceStatusName,
  getCustomerCommStatusBadgeClasses,
  getCustomerCommStatusName,
  getCustomerInvoiceStatusBadgeClasses,
  getCustomerInvoiceStatusName
} from '../../services/statusSystemV2'

type StatusBadgeType = 'assignment' | 'tg-work' | 'tg-invoice' | 'customer-comm' | 'customer-invoice' | 'custom'

interface StatusBadgeProps {
  type: StatusBadgeType
  status: string
  size?: 'sm' | 'md' | 'lg'
  icon?: ReactNode
  onClick?: () => void
  className?: string
  // For custom type only
  customLabel?: string
  customClassName?: string
}

export function StatusBadge({
  type,
  status,
  size = 'md',
  icon,
  onClick,
  className: extraClassName,
  customLabel,
  customClassName
}: StatusBadgeProps) {
  let badgeClasses: string
  let label: string

  switch (type) {
    case 'assignment':
      badgeClasses = getAssignmentStatusBadgeClassesV2(status as AssignmentStatusV2)
      label = getAssignmentStatusNameV2(status as AssignmentStatusV2)
      break
    case 'tg-work':
      badgeClasses = getTgWorkStatusBadgeClasses(status as TgWorkStatus)
      label = getTgWorkStatusName(status as TgWorkStatus)
      break
    case 'tg-invoice':
      badgeClasses = getTgInvoiceStatusBadgeClasses(status as TgInvoiceStatus)
      label = getTgInvoiceStatusName(status as TgInvoiceStatus)
      break
    case 'customer-comm':
      badgeClasses = getCustomerCommStatusBadgeClasses(status as CustomerCommStatus)
      label = getCustomerCommStatusName(status as CustomerCommStatus)
      break
    case 'customer-invoice':
      badgeClasses = getCustomerInvoiceStatusBadgeClasses(status as CustomerInvoiceStatus)
      label = getCustomerInvoiceStatusName(status as CustomerInvoiceStatus)
      break
    case 'custom':
      badgeClasses = customClassName || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
      label = customLabel || status
      break
    default:
      badgeClasses = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
      label = status
  }

  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-xs',
    md: 'px-2 py-0.5 text-xs',
    lg: 'px-2.5 py-1 text-sm'
  }

  const baseClasses = `inline-flex items-center gap-1 rounded-full font-medium ${sizeClasses[size]} ${badgeClasses}`
  const interactiveClasses = onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
  const finalClasses = `${baseClasses} ${interactiveClasses} ${extraClassName || ''}`

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={finalClasses}
      >
        {icon}
        {label}
      </button>
    )
  }

  return (
    <span className={finalClasses}>
      {icon}
      {label}
    </span>
  )
}

/**
 * Simple status badge - shorthand for StatusBadge type="assignment"
 */
interface SimpleStatusBadgeProps {
  status: string
  size?: 'sm' | 'md' | 'lg'
  onClick?: () => void
}

export function LegacyStatusBadge({ status, size = 'md', onClick }: SimpleStatusBadgeProps) {
  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-xs',
    md: 'px-2 py-0.5 text-xs',
    lg: 'px-2.5 py-1 text-sm'
  }

  const baseClasses = `inline-flex items-center rounded-full font-medium ${sizeClasses[size]} ${getAssignmentStatusBadgeClassesV2(status as AssignmentStatusV2)}`
  const interactiveClasses = onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${baseClasses} ${interactiveClasses}`}
      >
        {getAssignmentStatusNameV2(status as AssignmentStatusV2)}
      </button>
    )
  }

  return (
    <span className={baseClasses}>
      {getAssignmentStatusNameV2(status as AssignmentStatusV2)}
    </span>
  )
}
